import { DateTime } from 'luxon';
import { TIME_ZONE_UTC } from '@/constants';
import { EventListDisplay } from '@/types/gql.generated';
import { datePartInZone, parseIsoDate } from '@/utils/dates';
import type { DecoratedEvent, DecoratedExperienceEvent } from '../types';
import {
  eventOverlapsWindow,
  getEventLastEnd,
  type DateTimeWindow,
} from './range.lib';
import { filterUpcomingEvents } from './upcomingEvents.lib';

export type EventListSettings = {
  eventListDisplay: EventListDisplay;
  eventListStartDate?: string | null;
  eventListEndDate?: string | null;
};

const NAVIGATION_YEARS = 5;

export const normalizeEventListDisplay = (
  display: EventListDisplay
): EventListDisplay =>
  display === EventListDisplay.Past ? EventListDisplay.All : display;

// The frame both stored bounds live in, so a picked day and a saved one compare
// as calendar days rather than as instants.
export const toEventListDay = (date: DateTime): DateTime =>
  datePartInZone(date, TIME_ZONE_UTC);

// Midnight UTC, not a date-only string: the column is a DateTime and the server
// rejects the latter.
export const toEventListBound = (
  date: DateTime | null | undefined
): string | null => (date ? toEventListDay(date).toISO() : null);

const parseWindowDate = (
  iso: string | null | undefined,
  zone: string | undefined
): DateTime | undefined => {
  if (!iso) {
    return undefined;
  }
  const parsed = DateTime.fromISO(iso, { zone: 'utc' });
  const isoDate = parsed.isValid ? parsed.toISODate() : null;
  return isoDate ? DateTime.fromISO(isoDate, { zone }) : undefined;
};

// Only CUSTOM reads the saved dates: the other modes leave stale start/end
// values behind on the experience.
export const resolveEventListWindow = (
  experience: EventListSettings,
  displayTimezone?: string
): DateTimeWindow => {
  const display = normalizeEventListDisplay(experience.eventListDisplay);

  if (display === EventListDisplay.Upcoming) {
    return { start: DateTime.local({ zone: displayTimezone }).startOf('day') };
  }

  if (display !== EventListDisplay.Custom) {
    return {};
  }

  return {
    start: parseWindowDate(
      experience.eventListStartDate,
      displayTimezone
    )?.startOf('day'),
    end: parseWindowDate(experience.eventListEndDate, displayTimezone)?.endOf(
      'day'
    ),
  };
};

// Reads the two dates straight rather than going through
// resolveEventListWindow: the planner layout rejects eventListDisplay=CUSTOM, so
// ScheduleDatesSection saves its dates without it.
export const resolveScheduleDatesWindow = (
  dates: Pick<EventListSettings, 'eventListStartDate' | 'eventListEndDate'>,
  displayTimezone?: string
): DateTimeWindow => ({
  start: parseWindowDate(dates.eventListStartDate, displayTimezone)?.startOf(
    'day'
  ),
  end: parseWindowDate(dates.eventListEndDate, displayTimezone)?.endOf('day'),
});

export const intersectWindows = (
  a: DateTimeWindow,
  b: DateTimeWindow
): DateTimeWindow => ({
  start:
    a.start && b.start ? DateTime.max(a.start, b.start) : (a.start ?? b.start),
  end: a.end && b.end ? DateTime.min(a.end, b.end) : (a.end ?? b.end),
});

// The grouping pipeline's content bounds, as distinct from the canvas dates a
// template resolves from the experience, which are snapped out to whole periods.
export const toContentOptions = (
  window: DateTimeWindow | undefined
): { contentStartDate?: string; contentEndDate?: string } => ({
  contentStartDate: window?.start?.toISODate() ?? undefined,
  contentEndDate: window?.end?.toISODate() ?? undefined,
});

/** Whether a window's bounds are reversed, i.e. it describes no time at all. */
export const isEmptyWindow = ({ start, end }: DateTimeWindow): boolean =>
  Boolean(start && end && start > end);

/**
 * The CANVAS a planner view draws: its own "What to show" setting, capped by the
 * planner's schedule dates.
 *
 * Falls back to the schedule window when the two don't overlap, rather than
 * returning the reversed intersection, which would leave the view trapped on a
 * period whose pager menu is empty and whose canvas generates no cells.
 * useEventListWindow deliberately does NOT route its content window through
 * here, so the widened canvas stays empty.
 */
export const resolveViewWindow = (
  settings: EventListSettings,
  scheduleDates: Pick<
    EventListSettings,
    'eventListStartDate' | 'eventListEndDate'
  >,
  displayTimezone?: string
): DateTimeWindow => {
  const scheduleWindow = resolveScheduleDatesWindow(
    scheduleDates,
    displayTimezone
  );
  const window = intersectWindows(
    resolveEventListWindow(settings, displayTimezone),
    scheduleWindow
  );

  return isEmptyWindow(window) ? scheduleWindow : window;
};

/**
 * The navigable bounds for a paged layout: the view's own window, widened by
 * NAVIGATION_YEARS on whichever side it leaves open.
 *
 * Each open side is measured from the specified one, not from today. Anchoring
 * both to today independently reverses the bounds whenever the window sits
 * entirely outside now ± NAVIGATION_YEARS, and reversed bounds leave the pager's
 * year menu empty.
 */
export const getEventListBounds = (
  experience: EventListSettings,
  displayTimezone?: string
): { start: DateTime; end: DateTime } => {
  const { start, end } = intersectWindows(
    resolveEventListWindow(experience, displayTimezone),
    // FULL renders a configured span in its entirety, so the pager must not
    // offer periods outside it. Kept out of resolveEventListWindow, which also
    // drives the event filter, where FULL means "no filter".
    normalizeEventListDisplay(experience.eventListDisplay) ===
      EventListDisplay.Full
      ? resolveScheduleDatesWindow(experience, displayTimezone)
      : {}
  );
  const now = DateTime.local({ zone: displayTimezone });

  return {
    start:
      start ??
      DateTime.min(now, end ?? now)
        .minus({ years: NAVIGATION_YEARS })
        .startOf('year'),
    end:
      end ??
      DateTime.max(now, start ?? now)
        .plus({ years: NAVIGATION_YEARS })
        .endOf('year'),
  };
};

/**
 * An anchor date as the same calendar day in the display zone, clamped to the
 * navigable bounds. A `?range=` anchor is parsed in the browser's zone, so
 * periods built from it as-is start off the display zone's midnight; behind it,
 * the window opens after the decoded end of every timed event on its first day.
 */
export const clampAnchorToEventListBounds = (
  anchor: DateTime,
  experience: EventListSettings,
  displayTimezone: string
): DateTime => {
  const { start, end } = getEventListBounds(experience, displayTimezone);
  const local = anchor.setZone(displayTimezone, { keepLocalTime: true });
  return local < start ? start : local > end ? end : local;
};

/**
 * The year a date-range picker's year menu should open on, clamped to the years
 * it offers.
 */
export const getDefaultPickerYear = ({
  value,
  startYear,
  endYear,
  eventListStartDate,
  eventListEndDate,
  now,
}: {
  value: { startDate: DateTime } | null | undefined;
  startYear: number;
  endYear: number;
  eventListStartDate?: string | null;
  eventListEndDate?: string | null;
  /** Taken rather than read, so the caller's display timezone decides the year. */
  now: DateTime;
}): number => {
  const clamp = (year: number) => Math.min(Math.max(year, startYear), endYear);

  if (value) return clamp(value.startDate.year);

  if (now.year >= startYear && now.year <= endYear) return now.year;

  // parseIsoDate, not fromISO: the bounds are stored as midnight UTC, which a
  // bare parse resolves to the previous day west of UTC.
  const end = parseIsoDate(eventListEndDate);
  if (end && end < now) return clamp(end.year);

  const start = parseIsoDate(eventListStartDate);
  if (start && start > now) return clamp(start.year);

  return startYear;
};

/**
 * Trim an event list to the view's "What to show" window.
 *
 * Ordering differs by mode, deliberately. UPCOMING sorts by each event's *next*
 * occurrence, since a weekly series stores its first as startDate; ALL and
 * CUSTOM preserve the incoming order, where a series belongs at its actual start.
 */
export const filterEventsByEventListDisplay = (
  events: DecoratedExperienceEvent[],
  experience: EventListSettings,
  displayTimezone: string
): DecoratedExperienceEvent[] => {
  const display = normalizeEventListDisplay(experience.eventListDisplay);

  if (display === EventListDisplay.Upcoming) {
    return filterUpcomingEvents(events, displayTimezone);
  }

  if (display !== EventListDisplay.Custom) {
    return events;
  }

  const window = resolveEventListWindow(experience, displayTimezone);
  if (!window.start && !window.end) {
    return events;
  }

  return events.filter((event) => eventOverlapsWindow(event, window));
};

// lastEndDate is server-computed and always populated, so for a rule with
// neither UNTIL nor COUNT it reports a horizon the server chose rather than
// anything the author asked for.
const isOpenEndedSeries = (event: DecoratedEvent): boolean =>
  Boolean(event.rrule) && !/(?:UNTIL|COUNT)=/i.test(event.rrule ?? '');

/**
 * The span the events themselves cover, for suggesting "First event" / "Last
 * event" defaults in a date-range picker. Inclusive at both ends, so the last
 * covered day steps back from an all-day event's exclusive end.
 */
export const getEventDateBounds = (
  events: DecoratedEvent[]
): DateTimeWindow => {
  let start: DateTime | undefined;
  let end: DateTime | undefined;

  for (const event of events) {
    if (!start || event.startDate < start) {
      start = event.startDate;
    }
    const eventEnd = isOpenEndedSeries(event)
      ? event.endDate
      : getEventLastEnd(event);
    const lastCoveredDay = event.isAllDay
      ? eventEnd.minus({ days: 1 })
      : eventEnd;
    if (!end || lastCoveredDay > end) {
      end = lastCoveredDay;
    }
  }

  return { start: start?.startOf('day'), end: end?.startOf('day') };
};
